import { ChangeDetectorRef, Component, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy, inject, input, linkedSignal } from '@angular/core';
import {
  CourseItem,
  QdmService,
  RankItem,
  ServiceItem,
} from 'app/Services/qdm.service';
import { PipeNumberFormat } from '../Pipes/PipeNumberFormat';
import { EnvLoader } from 'app/Environment/EnvLoader';

import { AddToCartButtonComponent } from '../AddToCartButton/AddToCartButton.component';
import { CartItem, CartService } from 'app/Cart/CartService';
import { Subscription } from 'rxjs';
import { ModalService } from 'app/Services/modal.service';
import { ModalCourseDetailsComponent } from '../Modal/ModalCourseDetails/ModalCourseDetails.component';
import { RestaurantHelper } from 'app/Utils/RestaurantHelper';
import { AllergensIndicatorComponent } from '../AllergensIndicator/AllergensIndicator.component';
import { IsAllergenPresentPipe } from '../Pipes/IsAllergenPresentPipe';
import { FullAdmissionsData, GestionService } from 'app/Services/gestion.service';
import { WeekMenuDisplayComponent } from '../WeekMenuDisplay/WeekMenuDisplay.component';
import { AdmissionService } from 'app/Services/admission.service';

@Component({
    selector: 'course-card',
    imports: [
    PipeNumberFormat,
    AddToCartButtonComponent,
    AddToCartButtonComponent,
    AllergensIndicatorComponent,
    IsAllergenPresentPipe
],
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
    @if (this.courseItem(); as courseItem) {
      <div
        [class.border-orange-1]="this.isPresentOnCart"
        [class.border-2]="this.cartItem && this.cartItem.quantity > 0"
        class="bg-white rounded shadow overflow-hidden min-w-64 hover:bg-gris-3 cursor-pointer border border-gris-3 w-64"
        (click)="this.openCourseDetails()"
        >
        <div class="relative flex flex-col h-full">
          <div class="w-full h-32 relative" [id]="'image-container-' + courseItem.id">
            <img
            [attr.src]="
              this.QDM_URL +
              '/upload/course_images/' +
              (this.currentServiceState()?.rst_id || 0) +
              '/image_' +
              courseItem.id
            "
              [style.backgroundColor]="getBackgroundColor()"
              (error)="this.onImageError($event)"
              [id]="'course-image-' + courseItem.id"
              class="w-full h-full object-cover"
              />
              @if (courseItem.allergens.length > 0) {
                <allergens-indicator
            [isAllergenPresent]="
              courseItem.allergens | isAllergenPresent : markedAllergens()
            "
                  class="absolute right-2 bottom-2"
                  />
              }
            </div>
            @if (courseItem.labels.length > 0) {
              <div
                class="flex p-2 gap-1 pb-1"
                >
                @for (label of courseItem.labels; track label) {
                  <span
                    [style.backgroundColor]="label.color"
                    class="text-white px-2 py-1 rounded-full text-xs font-medium flex items-center"
                    >
                    {{ label.name }}
                  </span>
                }
              </div>
            }
            <div class="px-1 pb-1 flex-1 flex flex-col">
              <h3
                class="text-sm text-gray-800 p-2 max-w-full overflow-hidden max-w-64 text-center"
                [class.font-semibold]="!this.boldCourses()"
                [innerText]="courseItem.name"
                [class.uppercase]="this.uppercaseCourses()"
                [class.font-bold]="this.boldCourses()"
                [class.tracking-widest]="this.dyslexie()"
              ></h3>
              <div class="mt-auto pt-2 flex justify-between items-center">
                @if (this.admission ? (this.isAcceptPayment && this.isExtra()) : (this.isAcceptPayment)) {
                  <span
                    class="text-gray-700 font-bold min-w-16 text-xs fg-gris-1u px-2"
                    [innerText]="(courseItem.price || 0 | formatNumber) + '€'"
                  ></span>
                }
                <add-to-cart-button
                  class="ml-auto w-[8.5rem] h-8 rounded block"
                  [restaurantId]="this.restaurantHelper?.restaurant.id || 0"
                  [serviceId]="this.currentServiceState()?.id || 0"
                  [childId]="this.childIdState()"
                  [cartItem]="this.cartItem"
                  [remainingQuantity]="this.getRemainingQuantity()"
                  (onQuantityChanged)="this.updatePresenceInCart($event)"
                  />
                </div>
              </div>
              @if (courseItem.isMeatFullCourse || courseItem.isVeganFullCourse) {
                <span class="text-xs text-gray-500 font-bold uppercase absolute top-2 left-2 bg-white px-2 py-1 rounded-full">
                  Plat complet {{courseItem.isMeatFullCourse ? 'viande' : 'légume'}}
                </span>
              }
            </div>
          </div>
        }
    `
})
export class CourseCardComponent implements OnInit, OnChanges, OnDestroy {
  private cart = inject(CartService);
  private cdr = inject(ChangeDetectorRef);
  private qdm = inject(QdmService);
  private modal = inject(ModalService);
  private gestion = inject(GestionService);
  private admissionService = inject(AdmissionService);

  readonly courseItem = input.required<CourseItem | undefined>();
  readonly rankItem = input.required<RankItem | undefined>();
  readonly currentService = input.required<ServiceItem | undefined>();
  /** Valeur effective : initialisée par l'input, surchargée par le state global */
  protected readonly currentServiceState = linkedSignal(() => this.currentService());
  readonly childId = input.required<number>();
  /** Valeur effective : initialisée par l'input, surchargée par le state global */
  protected readonly childIdState = linkedSignal(() => this.childId());
  readonly date = input.required<Date>();

  readonly uppercaseCourses = input<boolean>(false);
  readonly boldCourses = input<boolean>(false);
  readonly dyslexie = input<boolean>(false);

  readonly markedAllergens = input<number[]>([]);

  cartItem: CartItem | undefined = undefined;

  isPresentOnCart: boolean = false;

  protected QDM_URL: string = '';
  private sub: Subscription = new Subscription();

  isAcceptPayment: boolean = false;
  restaurantHelper: RestaurantHelper | undefined = undefined;

  readonly mode = input.required<'week' | 'day'>();
  
  readonly iconsServices = input<{
    [key: number]: {
        url: string;
        bg: string;
    };
}>({});

  readonly isExtra = input<boolean>(false);
  
  private readonly ALT_BACKGROUND_COLORS = [
    '#F8B133', // orange-1
    '#8ABD27', // vert-1
    '#E57373', // rose-1
    '#64B5F6', // bleu-1
    '#FFB74D', // orange-3
    '#81C784', // vert-3
    '#F48FB1', // rose-3
  ];

  imagesWithErrors: Set<number> = new Set();
  admission: FullAdmissionsData | null = null;

  ngOnInit(): void {
    this.QDM_URL = EnvLoader.load().qdmApiUrl;
    this.updateCartItem();

    this.sub.add(this.qdm.selectedAdmission$.subscribe((admission) => {
      this.admission = admission;
      this.cdr.detectChanges();
      this.updateCartItem();
    }));

    this.sub.add(
      this.qdm.selectedRestaurant$.subscribe((rst) => {
        this.qdm.fetchRestaurantById(rst?.id || 0).then((rstFull) => {
          this.restaurantHelper = new RestaurantHelper(rstFull);
          this.cdr.detectChanges();
          this.updateCartItem();
        });
      })
    );

    this.sub.add(
      this.qdm.selectedEnterprise$.subscribe((ent) => {
        this.isAcceptPayment = ent?.isAcceptPayment || false;
        this.cdr.detectChanges();
        this.updateCartItem();
      })
    );

    this.sub.add(
      this.qdm.currentService$.subscribe((svc) => {
        this.currentServiceState.set(svc);
        this.updateCartItem();
      })
    );

    this.sub.add(
      this.qdm.selectChild$.subscribe((child) => {
        this.childIdState.set(child?.id || -1);
        this.updateCartItem();
      })
    );

    this.sub.add(
      this.cart.cart$.subscribe((cart) => {
        // Ne traite les données que si courseItem existe
        const courseItem = this.courseItem();
        if (!courseItem) return;
        if(!this.date())return;

        const courseId = courseItem.id;

        // Filtre le panier pour obtenir uniquement les éléments pertinents
        const filteredCart = cart.filter(
          (c) =>
            c.childId === this.childIdState() &&
            c.date === this.date().formatDate('forApi') &&
            c.serviceId === this.currentServiceState()?.id
        );

        // Recherche de l'item dans le panier filtré
        let isPresent = false;

        for (const cartItem of filteredCart) {
          if (cartItem.items && Array.isArray(cartItem.items)) {
            const foundItem = cartItem.items.find(
              (item) => item.id === courseId && item.quantity > 0
            );
            if (foundItem) {
              isPresent = true;
              break;
            }
          }
        }

        // Mise à jour de l'état
        this.isPresentOnCart = isPresent;
        this.cdr.detectChanges();
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['courseItem']) {
      this.updateCartItem();
    }

    if (changes['rankItem']) {
      this.updateCartItem();
    }

    // Si les icônes ont changé, vérifier si nous devons mettre à jour les images qui ont déjà rencontré des erreurs
    const courseItem = this.courseItem();
    if (changes['iconsServices'] && courseItem && this.imagesWithErrors.has(courseItem.id)) {
      this.refreshImageWithIcon();
    }
  }

  updateIfPresentInCart() {
    if (!this.cartItem) {
      this.isPresentOnCart = false;
      this.cdr.detectChanges();
      return;
    }

    if(!this.date())return;

    const courseId = this.cartItem.id;

    // Filtre le panier pour obtenir uniquement les éléments pertinents
    const filteredCart = this.cart.cartSubject.value.filter(
      (c) =>
        c.childId === this.childIdState() &&
        c.date === this.date().formatDate('forApi') &&
        c.serviceId === this.currentServiceState()?.id
    );

    // Recherche de l'item dans le panier filtré
    let isPresent = false;

    for (const cartItem of filteredCart) {
      if (cartItem.items && Array.isArray(cartItem.items)) {
        const foundItem = cartItem.items.find(
          (item) => item.id === courseId && item.quantity > 0
        );
        if (foundItem) {
          isPresent = true;
          break;
        }
      }
    }

    // Mise à jour de l'état
    this.isPresentOnCart = isPresent;
    this.cdr.detectChanges();
  }

  updateCartItem() {
    const courseItem = this.courseItem();
    const rankItem = this.rankItem();
    if (!courseItem || !rankItem) {
      this.cartItem = undefined;
      this.updateIfPresentInCart();
      this.cdr.detectChanges();
      return;
    }

    this.cartItem = this.cart.toCartItem(courseItem, rankItem);
    this.updateIfPresentInCart();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  openCourseDetails() {
    this.modal.open(
      ModalCourseDetailsComponent,
      {
        course: this.courseItem(),
        service: this.currentServiceState(),
        restaurantId: this.currentServiceState()?.rst_id || 0,
        childId: this.childIdState(),
        date: this.date(),
        rank: this.rankItem(),
        restaurantHelper: this.restaurantHelper,
        mode: this.mode(),
        iconsServices: this.iconsServices()
      },
      () => {}
    );
  }

  updatePresenceInCart(data: { cartItem: CartItem; qty: number }) {
    this.isPresentOnCart = data.qty > 0;
    this.cdr.detectChanges();
  }

  getRemainingQuantity(): number {
    if(!this.qdm.selectedAdmissionSubject.value || this.isExtra())
      return 999;

    const rankItem = this.rankItem();
    return this.admissionService.getRemainingQuantity(
      this.currentServiceState()?.id,
      rankItem?.family_id || rankItem?.id || 0,
      this.restaurantHelper?.restaurant.id,
      this.childIdState(),
      this.date(),
      this.qdm.groupOrderQuantitySubject.value,
      this.qdm.selectedAdmissionSubject.value
    );
  }

  getBackgroundColor(): string {
    const currentService = this.currentServiceState();
    const rankItem = this.rankItem();
    if (!rankItem || !currentService) return 'transparent';
    
    const icon = this.iconsServices()[rankItem.family_id || rankItem.id || 0];
    
    return (
      (icon?.bg ? `#${icon.bg}` : undefined) ||
      this.ALT_BACKGROUND_COLORS[
        currentService.id % this.ALT_BACKGROUND_COLORS.length
      ]
    );
  }

  onImageError(ev: ErrorEvent) {
    const courseItem = this.courseItem();
    if (!courseItem) return;
    
    const target = ev.target as HTMLImageElement;
    this.imagesWithErrors.add(courseItem.id);
    
    // Remplacer l'image
    this.replaceImageWithIcon(target);
  }

  private replaceImageWithIcon(targetElement: HTMLImageElement) {
  const courseItem = this.courseItem();
  const rankItem = this.rankItem();
  if (!courseItem || !rankItem) return;
  
  const familyId = rankItem.family_id || rankItem.id || 0;
  const icon = this.iconsServices()[familyId];
  
  // Obtenir le parent de l'image
  const containerDiv = targetElement.parentElement;
  if (!containerDiv) return;
  
  // Sauvegarder l'indicateur d'allergènes s'il existe
  const allergenIndicator = containerDiv.querySelector('allergens-indicator');
  
  // Vider le conteneur mais garder une référence à l'indicateur
  containerDiv.innerHTML = '';
  
  if (!icon || !icon.bg || !icon.url) {
    // Fallback vers l'image par défaut si les icônes ne sont pas disponibles
    const imgElement = document.createElement('img');
    imgElement.src = WeekMenuDisplayComponent.IMAGE_NOT_FOUND_URL;
    imgElement.className = 'w-full h-full object-cover';
    imgElement.alt = courseItem.name;
    containerDiv.appendChild(imgElement);
  } else {
    // Créer un nouveau div avec l'image d'icône
    const iconDiv = document.createElement('div');
    iconDiv.style.backgroundColor = `#${icon.bg}`;
    iconDiv.className = 'w-full h-full object-cover relative overflow-hidden';
    iconDiv.innerHTML = `
      <img src="${icon.url}" class="w-24 object-cover absolute bottom-0 left-0 -translate-x-4 translate-y-4" alt="Icon" />
    `;
    containerDiv.appendChild(iconDiv);
  }
  
  // Remettre l'indicateur d'allergènes s'il existait
  if (allergenIndicator) {
    containerDiv.appendChild(allergenIndicator);
  }
  
  this.cdr.detectChanges();
}

private refreshImageWithIcon() {
  const courseItem = this.courseItem();
  if (!courseItem) return;
  
  const containerId = `image-container-${courseItem.id}`;
  const container = document.getElementById(containerId);
  
  if (container) {
    const imageElement = document.getElementById(`course-image-${courseItem.id}`) as HTMLImageElement;
    
    if (imageElement) {
      // L'image existe encore, ce qui signifie qu'elle n'a pas été remplacée
      return;
    }
    
    // Sauvegarder l'indicateur d'allergènes s'il existe
    const allergenIndicator = container.querySelector('allergens-indicator');
    
    // L'image a déjà été remplacée, mettre à jour avec les nouvelles icônes
    container.innerHTML = ''; // Vider le conteneur
    
    const rankItem = this.rankItem();
    const familyId = rankItem?.family_id || rankItem?.id || 0;
    const icon = this.iconsServices()[familyId];
    
    if (!icon || !icon.bg || !icon.url) {
      // Fallback vers l'image par défaut
      const imgElement = document.createElement('img');
      imgElement.src = WeekMenuDisplayComponent.IMAGE_NOT_FOUND_URL;
      imgElement.className = 'w-full h-full object-cover';
      imgElement.alt = courseItem.name || '';
      container.appendChild(imgElement);
    } else {
      // Créer un div avec l'icône
      const iconDiv = document.createElement('div');
      iconDiv.style.backgroundColor = `#${icon.bg}`;
      iconDiv.className = 'w-full h-full object-cover relative overflow-hidden';
      iconDiv.innerHTML = `
        <img src="${icon.url}" class="w-24 object-cover absolute bottom-0 left-0 -translate-x-4 translate-y-4" alt="Icon" />
      `;
      container.appendChild(iconDiv);
    }
    
    // Remettre l'indicateur d'allergènes s'il existait
    if (allergenIndicator) {
      container.appendChild(allergenIndicator);
    }
    
    this.cdr.detectChanges();
  }
}
}